/** eslint-disable @typescript-eslint/no-unused-vars */
import {
  PaintBlockFiltersInput,
  useDefectsAreaForCustomBlocksQuery,
  useSavedBlockPoIsQuery,
} from '@/__generated__/graphql';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import * as state from '@/components/Analysis/state';
import * as localState from '../state';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { CustomBlockListItem } from './CustomBlockListItem';
import { CustomBlockListHeader } from './CustomBlockListHeader';
import { CustomBlockListSearch } from './CustomBlockListSearch';
import { CustomBlockSortingHeader } from './CustomBlockSortingHeader';
import { extractErrorMessageFromUnknownError } from '@/utils/error';
import { PageLoader } from '@/components/shared/PageLoader';
import { CustomSavedBlocksListItem } from './CustomSavedBlocksListItem';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { CustomBlockFilterChips } from './CustomBlockFilterChips';

const LIMIT_SIZE = 50;

export const CustomBlockList = () => {
  const structureId = useGetStructureId();
  const selectedPaintRegionGroup = useAtomValue(state.selectedPaintRegionGroup);
  const [searchTerm, setSearchTerm] = useState('');
  const [isRefetching, setIsRefetching] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const [customBlocksQueryInput, setCustomBlocksQueryInput] = useAtom(
    localState.customBlocksQueryInput
  );
  const customBlockQueryFilters = useAtomValue(localState.customBlockQueryFilters);

  const setSelectedSavedBlockId = useSetAtom(localState.selectedSavedBlockId);
  const setAllSavedBlocks = useSetAtom(localState.allSavedBlocks);

  const isSavedBlocksSelected = useAtomValue(state.isSavedBlocksSelected);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const listContainerRef = useRef<HTMLDivElement>(null);

  const queryInput: PaintBlockFiltersInput = useMemo(() => {
    return {
      structureIds: [structureId],
      paintRegionGroupIds: selectedPaintRegionGroup?.id ? [selectedPaintRegionGroup.id] : undefined,
      ...customBlockQueryFilters,
    };
  }, [selectedPaintRegionGroup?.id, structureId, customBlockQueryFilters]);
  const { data, loading, error, refetch } = useDefectsAreaForCustomBlocksQuery({
    variables: {
      input: queryInput,
      pageInfo: {
        skip: 0,
        limit: LIMIT_SIZE,
        sortBy: customBlocksQueryInput.sortBy,
        sortDir: customBlocksQueryInput.sortDir,
      },
    },
    skip: isSavedBlocksSelected,
  });

  // Only call the savedBlockPOIs query if saved blocks are selected
  const {
    data: savedBlocksData,
    loading: savedBlocksLoading,
    error: savedBlocksError,
  } = useSavedBlockPoIsQuery({
    variables: {
      input: {
        structureId,
        type: 'WORK_SCOPE',
      },
    },
    skip: !isSavedBlocksSelected,
    onCompleted: (res) => {
      setAllSavedBlocks(res.pointOfInterests);
    },
  });

  const refetchWithSorting = async (
    sortByParam: string | undefined,
    sortDirParam: number | undefined
  ) => {
    setIsRefetching(true);
    setCustomBlocksQueryInput((prev) => ({
      ...prev,
      page: 2,
      sortBy: sortByParam,
      sortDir: sortDirParam,
    }));
    try {
      await refetch({
        input: queryInput,
        pageInfo: {
          skip: 0,
          limit: LIMIT_SIZE,
          sortBy: sortByParam,
          sortDir: sortDirParam,
        },
      }).then(() => {});
    } catch (err) {
      const message = extractErrorMessageFromUnknownError(err);
      console.error('Error refetching custom blocks with sorting', message);
    } finally {
      setIsRefetching(false);
    }
  };

  const loadMore = useCallback(async () => {
    if (loading || isLoadingMore || !customBlocksQueryInput.hasMoreItems) return;

    setIsLoadingMore(true);
    try {
      const result = await refetch({
        input: queryInput,
        pageInfo: {
          skip: 0,
          limit: LIMIT_SIZE * customBlocksQueryInput.page,
          sortBy: customBlocksQueryInput.sortBy,
          sortDir: customBlocksQueryInput.sortDir,
        },
      });

      // Check if new items were actually loaded
      const newItems =
        result.data?.defectsAreaForAssemblyPaintRegionsByGroup?.defectsByPaintBlock || [];

      if (newItems.length < LIMIT_SIZE * customBlocksQueryInput.page) {
        setCustomBlocksQueryInput((prev) => ({
          ...prev,
          hasMoreItems: false,
        }));
      }

      // Slightly adjust scroll position after new items are loaded
      if (listContainerRef.current) {
        listContainerRef.current.scrollTop -= 100;
      }

      setCustomBlocksQueryInput((prev) => ({
        ...prev,
        page: prev.page + 1,
      }));
    } catch (err) {
      const message = extractErrorMessageFromUnknownError(err);
      console.error('Error loading more custom blocks', message);
    } finally {
      setIsLoadingMore(false);
    }
  }, [
    loading,
    isLoadingMore,
    customBlocksQueryInput.hasMoreItems,
    customBlocksQueryInput.page,
    customBlocksQueryInput.sortBy,
    customBlocksQueryInput.sortDir,
    refetch,
    queryInput,
    setCustomBlocksQueryInput,
  ]);

  function clickHandler(poiId: string | undefined) {
    setSelectedSavedBlockId(poiId);
  }

  // Infinite scroll observer
  useEffect(() => {
    const loadMoreRefCurrent = loadMoreRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && customBlocksQueryInput.hasMoreItems) {
          loadMore();
        }
      },
      { threshold: 1 }
    );

    if (loadMoreRefCurrent) {
      observer.observe(loadMoreRefCurrent);
    }

    return () => {
      if (loadMoreRefCurrent) {
        observer.unobserve(loadMoreRefCurrent);
      }
    };
  }, [customBlocksQueryInput.hasMoreItems, loadMore]);

  const blocks = useMemo(() => {
    const items = data?.defectsAreaForAssemblyPaintRegionsByGroup?.defectsByPaintBlock || [];
    if (!searchTerm) return items;
    const filteredItems = items.filter((item) => {
      return item.name.toLowerCase().includes(searchTerm.toLowerCase());
    });
    return filteredItems;
  }, [data?.defectsAreaForAssemblyPaintRegionsByGroup?.defectsByPaintBlock, searchTerm]);

  const totalBlocks = data?.defectsAreaForAssemblyPaintRegionsByGroup?.totalCount;

  if (error || savedBlocksError)
    return (
      <Box p={2}>
        <Typography variant="h6" color="error" mt={5} mb={3}>
          Please contact support
        </Typography>
        <Typography variant="h6" color="error" fontWeight={100}>
          Error:{'  '}
          {error?.message || savedBlocksError?.message || ''}
        </Typography>
      </Box>
    );

  // Early return if saved blocks are selected
  if (isSavedBlocksSelected) {
    if (savedBlocksLoading) {
      return (
        <Box p={2}>
          <PageLoader />
        </Box>
      );
    }

    const pois = savedBlocksData?.pointOfInterests ?? [];
    const total = savedBlocksData?.pointOfInterestsCount?.count ?? 0;
    if (pois.length === 0) {
      return <p>No saved blocks found</p>;
    }

    return (
      <Stack height={'100%'}>
        <>
          <CustomBlockListHeader count={total || undefined} mt={2} mb={1} px={1} />
          <CustomBlockListSearch
            onSearch={setSearchTerm}
            value={searchTerm}
            sx={{ px: 1, my: 2 }}
          />

          <Stack
            mb={2}
            sx={{
              overflowY: 'auto',
            }}
          >
            {pois.map((item) => (
              <CustomSavedBlocksListItem
                key={item.id}
                poi={item}
                onRowClick={() => clickHandler(item.id)}
              />
            ))}
          </Stack>
        </>
      </Stack>
    );
  }

  const noBlocksFound = blocks.length === 0 && !loading && !isRefetching;

  return (
    <Stack height={'100%'}>
      <CustomBlockFilterChips BoxProps={{ m: 1 }} />
      <CustomBlockListHeader count={totalBlocks || undefined} mt={2} mb={1} px={1} />
      <CustomBlockListSearch onSearch={setSearchTerm} value={searchTerm} sx={{ px: 1, my: 2 }} />
      <CustomBlockSortingHeader onSortChange={refetchWithSorting} />

      <Box hidden={!loading && !isRefetching} sx={{ position: 'relative', top: '58px' }}>
        <PageLoader />
      </Box>

      <Stack
        ref={listContainerRef}
        sx={{
          overflowY: 'auto',
        }}
        hidden={loading || isRefetching}
      >
        {blocks.map((item) => (
          <CustomBlockListItem
            key={item.paintRegionId}
            block={item}
            selectedDefectLabel={customBlocksQueryInput.sortBy}
          />
        ))}
        {customBlocksQueryInput.hasMoreItems && (
          <div
            ref={loadMoreRef}
            style={{ height: '5rem', marginBottom: '4rem', visibility: 'hidden' }}
          />
        )}
        {isLoadingMore && !noBlocksFound && (
          <Box my={3} display={'flex'} justifyContent={'center'}>
            <CircularProgress />
          </Box>
        )}
        {noBlocksFound && (
          <Box p={2}>
            <Typography variant="h5" color="info" textAlign="center" mt={5}>
              No blocks found
            </Typography>
          </Box>
        )}
      </Stack>
    </Stack>
  );
};
